import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../../environments/environment';
import type { GeneralActivityPreferences, MyActivityPreferences, SportGoal } from '../domains/activity-preferences';

export interface SportActivityPreferencesPayload extends GeneralActivityPreferences {
  goal?: SportGoal;
}

/**
 * The four routes behind "when, how and what for".
 *
 * Called directly rather than through the generated SDK, as the organizer
 * invoices are: regenerating the client from a running API rewrites hundreds
 * of files and would bury this feature in unrelated drift. The shapes it sends
 * and receives are declared in domains/activity-preferences.
 */
@Injectable({ providedIn: 'root' })
export class ActivityPreferencesService {
  constructor(private readonly http: HttpClient) {}

  private get url(): string {
    return `${environment.api}/api/v1/profile/activity-preferences`;
  }

  /** The general answer, plus one already-resolved line per sport practised. */
  getMine(): Observable<MyActivityPreferences> {
    return this.http.get<MyActivityPreferences>(this.url);
  }

  updateGeneral(preferences: GeneralActivityPreferences): Observable<unknown> {
    return this.http.put(this.url, preferences);
  }

  updateSport(sportId: string, preferences: SportActivityPreferencesPayload): Observable<unknown> {
    return this.http.put(`${this.url}/${sportId}`, preferences);
  }

  /**
   * Sends a sport back to the general answer.
   *
   * Not the same as saving an empty one: an emptied availability means "none
   * of these", and this means "whatever I answered in general".
   */
  clearSport(sportId: string): Observable<unknown> {
    return this.http.delete(`${this.url}/${sportId}`);
  }
}
